// Helpers
import { REQUEST_TYPES } from './index';

const buildOpenAMCredentialHeaders = ({ username, password } = {}) => ({
  ...(username ? { 'X-OpenAM-Username': username } : {}),
  ...(password ? { 'X-OpenAM-Password': password } : {}),
});

// params: OAuth authorize request query fields from customer app
// headers: X-Forwarded-Host for issuer derivation in ForgeRock
export const authorizeRequest = ({ params, forwardedHost, agencyId }) => ({
  type: '[API] Authorize',
  url: '/authorize',
  APIMethod: REQUEST_TYPES.GET,
  params: {
    ...params,
    agencyId: agencyId || '',
    applicationId: 'TxTSSO',
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

// body: { input: [{ name: 'IDToken0', value: '<device fingerprint json>' }], clientId, agencyId }
// headers: X-OpenAM-Username, X-OpenAM-Password
// params: authIndexType=service, authIndexValue=SSO_TREE
export const authenticateRequest = ({ deviceFingerprint, username, password, clientId, agencyId, forwardedHost, customerAppId }) => ({
  type: '[API] Authenticate',
  url: '/authenticate',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    input: [
      {
        name: 'IDToken0',
        value: deviceFingerprint,
      },
    ],
    clientId: clientId || '',
    agencyId: agencyId || '',
    applicationId: `TxTSSO_${customerAppId}`,
  },
  headers: {
    ...buildOpenAMCredentialHeaders({ username, password }),
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
  },
  params: {
    authIndexType: 'service',
    authIndexValue: 'SSO_TREE',
  },
});


// body: payload from /authenticate with IDToken2 updated to selected index
// Per backend contract, this endpoint uses callback payload only.
export const mfaChoicesRequest = ({ body, clientId, agencyId, userAliasId, forwardedHost, customerAppId }) => ({
  type: '[API] MFA Choices',
  url: '/mfaChoices',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    ...body,
    clientId: clientId || '',
    agencyId: agencyId || '',
    userAliasId: userAliasId || '',
    applicationId: `TxTSSO_${customerAppId}`,
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

// body: payload from /mfaChoices with IDToken1 updated to OTP and IDToken0 updated to remember-device choice
export const verifyOTPRequest = ({ body, clientId, agencyId, userAliasId, forwardedHost, customerAppId }) => ({
  type: '[API] Verify OTP',
  url: '/processOTP',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    ...body,
    clientId: clientId || '',
    agencyId: agencyId || '',
    userAliasId: userAliasId || '',
    applicationId: `TxTSSO_${customerAppId}`,
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

export const getCustomerAppInfoRequest = ({ agencyId, uniqueIdentifier, customerAppClientId, forwardedHost } = {}) => ({
  type: '[API] Get Customer App Info',
  url: '/getCustomerAppInfo',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    applicationId: 'TxTSSO',
    agencyId: agencyId || '',
    uniqueIdentifier,
    customerAppClientId,
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

export const verifyConsentRequest = ({ customerAppClientId, agencyId, userAliasId, uniqueIdentifier, forwardedHost, customerAppId } = {}) => ({
  type: '[API] Verify Consent',
  url: '/verifyConsent',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    applicationId: `TxTSSO_${customerAppId}`,
    agencyId: agencyId || '',
    uniqueIdentifier,
    customerAppClientId,
    userAliasId: userAliasId || '',
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

export const acceptConsentRequest = ({ userAliasId, customerAppClientId, agencyId, uniqueIdentifier, forwardedHost, customerAppId }) => ({
  type: '[API] Accept Consent',
  url: '/acceptConsent',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    applicationId: `TxTSSO_${customerAppId}`,
    agencyId: agencyId || '',
    uniqueIdentifier,
    userAliasId,
    customerAppClientId,
    consentType: 'Profile',
    accept: 'Y',
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

export const postOTPAuthorizeRequest = ({ clientId, agencyId, userAliasId, forwardedHost, customerAppId } = {}) => ({
  type: '[API] Post-OTP Authorize',
  url: '/authorize',
  APIMethod: REQUEST_TYPES.GET,
  params: {
    clientId: clientId || '',
    userAliasId: userAliasId || '',
    agencyId: agencyId || '',
    applicationId: `TxTSSO_${customerAppId}`,
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

export const logout = ({ clientId, agencyId, userAliasId, forwardedHost, customerAppId } = {}) => ({
  type: '[API] Logout',
  url: '/logout',
  APIMethod: REQUEST_TYPES.POST,
  body: {
    clientId: clientId || '',
    userAliasId: userAliasId || '',
    agencyId: agencyId || '',
    applicationId: `TxTSSO_${customerAppId}`,
  },
  headers: {
    ...(forwardedHost ? { 'X-Forwarded-Host': forwardedHost } : {}),
    Accept: 'application/json',
  },
});

